<script setup lang="ts">
import { computed, type Component } from 'vue'
import { useI18n } from 'vue-i18n'
import { CloseIcon } from '@solar-icons/vue/linear'

import AppButton from '../AppButton/AppButton.vue'
import { slotRenders } from '../slot-renders'
import { NOTICE_TONE_TABLE, type NoticeShape, type NoticeTone } from './notice-types'

const props = withDefaults(
  defineProps<{
    /** Where the notice lives: `inline` (page flow) · `card` · `banner` (sheet strip) · `toast` (default `inline`). */
    shape?: NoticeShape
    /** The semantic kind, which picks the glyph and its ink, and the wash (default `neutral`). */
    tone?: NoticeTone
    /** The one line shown when there is no `title`, or the second line when there is. */
    message: string
    /** An optional first line; adds a title in the tone's ink colour above `message`. */
    title?: string
    /** Overrides the tone's default glyph. */
    icon?: Component
    /** Extra caption lines under the message, in the text column, such as the checks behind a flag. */
    details?: readonly string[]
    /** Work the notice reports on: a fraction from 0 to 1, or `indeterminate` while the amount is unknown. Draws a thin bar in the tone's ink under the text. */
    progress?: number | 'indeterminate'
    /** A short count at the end of the progress bar, such as "1 of 4 done". */
    progressCaption?: string
    dismissible?: boolean
    /** The close button's accessible label; defaults to the shared "Dismiss" string. */
    dismissLabel?: string
  }>(),
  {
    shape: 'inline',
    tone: 'neutral',
    title: undefined,
    icon: undefined,
    details: () => [],
    progress: undefined,
    progressCaption: undefined,
    dismissible: false,
    dismissLabel: undefined,
  },
)

const emit = defineEmits<{
  dismiss: []
}>()

defineSlots<{
  /** Zero to two `AppButton`s before the close button: `md` (a quiet one `ghost`) in inline and card shapes, `size="xs"` in banner and toast shapes. */
  actions?(): unknown
  /** Extra content belonging to the notice, rendered full width under the glyph, text and actions row. */
  default?(): unknown
}>()

const { t } = useI18n()

const toneEntry = computed(() => NOTICE_TONE_TABLE[props.tone])
const glyph = computed(() => props.icon ?? toneEntry.value.glyph)
const spinGlyph = computed(() => !props.icon && props.tone === 'progress')
const resolvedDismissLabel = computed(() => props.dismissLabel ?? t('common.dismiss'))

const isToast = computed(() => props.shape === 'toast')
const hasTitleOnToast = computed(() => isToast.value && !!props.title)
const DENSE_SHAPES: readonly NoticeShape[] = ['banner', 'toast']
const actionSize = computed(() => (DENSE_SHAPES.includes(props.shape) ? 'xs' : 'md'))

const gapClass = computed(() => (props.shape === 'card' ? 'gap-8' : 'gap-6'))
const paddingClass = computed(() => {
  if (props.shape === 'card') return 'p-8'
  if (hasTitleOnToast.value) return 'p-6'
  return 'p-4'
})
const isMultiline = computed(() => Boolean(props.title) || props.details.length > 0)
const alignClass = computed(() => (isMultiline.value ? 'items-start' : 'items-center'))

const isIndeterminate = computed(() => props.progress === 'indeterminate')
const progressPercent = computed(() =>
  typeof props.progress === 'number'
    ? Math.round(Math.min(1, Math.max(0, props.progress)) * 100)
    : undefined,
)

function onDismiss() {
  emit('dismiss')
}
</script>

<template>
  <div
    :class="[
      'relative flex flex-col rounded-4',
      gapClass,
      paddingClass,
      isToast
        ? 'notice-island w-(--toast-width) max-w-[calc(100vw-var(--spacing-16))]'
        : toneEntry.washClass,
    ]"
    :role="isToast ? undefined : 'status'"
  >
    <span
      v-if="isToast"
      aria-hidden="true"
      class="pointer-events-none absolute inset-0 rounded-4"
      :class="toneEntry.washClass"
    />
    <div :class="['relative flex', alignClass, gapClass]">
      <span
        class="icon-md relative flex shrink-0 items-center"
        :class="[isMultiline ? 'h-(--text-body-sm--line-height)' : '', toneEntry.inkClass]"
        aria-hidden="true"
      >
        <component :is="glyph" :class="spinGlyph ? 'motion-safe:animate-spin' : ''" />
      </span>
      <div class="relative flex min-w-0 flex-1 flex-col gap-1">
        <template v-if="title">
          <span :class="['text-body-sm-medium', toneEntry.titleClass]">{{ title }}</span>
          <span class="text-caption text-text-secondary">{{ message }}</span>
        </template>
        <span v-else class="text-body-sm text-text-primary">{{ message }}</span>
        <span v-for="detail in details" :key="detail" class="text-caption text-text-secondary">{{
          detail
        }}</span>
        <div
          v-if="progress !== undefined"
          class="flex h-(--text-caption--line-height) items-center gap-8"
          :class="toneEntry.titleClass"
        >
          <span
            role="progressbar"
            :aria-label="title ?? message"
            :aria-valuemin="0"
            :aria-valuemax="100"
            :aria-valuenow="progressPercent"
            class="relative h-2 min-w-0 flex-1 overflow-hidden rounded-full bg-current/15"
          >
            <span
              v-if="isIndeterminate"
              class="absolute inset-y-0 start-0 w-1/4 rounded-full bg-current motion-safe:animate-indeterminate"
            />
            <span
              v-else
              class="absolute inset-y-0 start-0 rounded-full bg-current transition-[width] duration-slow ease-soft motion-reduce:transition-none"
              :style="{ width: `${progressPercent}%` }"
            />
          </span>
          <span v-if="progressCaption" class="shrink-0 text-caption tabular-nums">{{
            progressCaption
          }}</span>
        </div>
      </div>
      <div v-if="$slots.actions || dismissible" class="relative flex shrink-0 items-center gap-2">
        <slot name="actions" />
        <AppButton
          v-if="dismissible"
          icon-only
          variant="ghost"
          :size="actionSize"
          :start-icon="CloseIcon"
          :aria-label="resolvedDismissLabel"
          @click="onDismiss"
        />
      </div>
    </div>
    <div v-if="slotRenders($slots.default)" class="relative">
      <slot />
    </div>
  </div>
</template>
